import { BillingMethodOption } from '@/types/types';

// Mirrors BillingInformationValidator / SuperAdminUpdateBillingInformationValidator in the API so
// mistakes are caught before the round trip. The API stays the source of truth.

export enum BillingMethodId {
    MailInvoice = 1,
    EmailInvoice = 2,
    CreditCard = 3,
    ElectronicInvoice = 4,
}

// Plans that can be sold. A company on any other plan can be edited as long as its plan is left alone.
export const SELLABLE_LICENSE_PLAN_IDS = [2, 3];
export const MAX_PAYMENT_TERMS_DAYS = 120;

export type BillingFormValues = {
    Name: string;
    FirstName: string;
    LastName: string;
    PhoneNumber: string;
    ContactEmail: string;
    Attention: string;
    Street1: string;
    Street2: string;
    ZipCode: string;
    City: string;
    CountryId: string;
    Email: string;
    BillingMethodId: number | '';
    LicensePlanId: number | '';
    PaymentTermsDays: number | '';
    VatRegistrationNumber: string;
    GLN: string;
    ReferenceLine1: string;
    ReferenceLine2: string;
};

export type BillingFormErrors = Partial<Record<keyof BillingFormValues, string>>;

export type ValidationContext = {
    // Billing methods the API offers, with the countries each is valid for. Skipped while not loaded.
    billingMethods?: BillingMethodOption[];
    // Plan the company is on today, which stays selectable even if it is no longer sellable.
    currentLicensePlanId?: number;
};

const NAME_PATTERN = new RegExp("^[\\p{L}]+([\\-' ][\\p{L}]+)*$", 'u');
const CITY_PATTERN = new RegExp("^[\\p{L}][\\p{L} \\-']{1,39}$", 'u');
const PHONE_CHARACTERS = /^\+?[0-9\s\-().]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const blank = (value: unknown) => value === undefined || value === null || `${value}`.trim() === '';

export const isValidZipCode = (zipCode: string, countryId: string) => {
    const digits = zipCode.replace(/ /g, '');
    // The API compares the country case sensitively, so does this.
    return countryId === 'FI' ? /^\d{5}$/.test(digits) : /^\d{3,5}$/.test(digits);
};

export const validateBillingInformation = (
    values: BillingFormValues,
    { billingMethods, currentLicensePlanId }: ValidationContext = {}
): BillingFormErrors => {
    const errors: BillingFormErrors = {};
    const countryId = values.CountryId?.trim() ?? '';
    const methodId = values.BillingMethodId === '' ? 0 : Number(values.BillingMethodId);

    if (blank(values.Name)) errors.Name = 'Name is required';
    else if (values.Name.trim().length > 80) errors.Name = 'Name can be at most 80 characters';

    if (blank(values.FirstName)) errors.FirstName = 'First name is required';
    else if (values.FirstName.trim().length > 50)
        errors.FirstName = 'First name can be at most 50 characters';
    else if (!NAME_PATTERN.test(values.FirstName.trim()))
        errors.FirstName = 'First name can only contain letters, spaces, hyphens and apostrophes';

    if (blank(values.LastName)) errors.LastName = 'Last name is required';
    else if (values.LastName.trim().length > 50)
        errors.LastName = 'Last name can be at most 50 characters';
    else if (!NAME_PATTERN.test(values.LastName.trim()))
        errors.LastName = 'Last name can only contain letters, spaces, hyphens and apostrophes';

    if (!blank(values.PhoneNumber)) {
        const phone = values.PhoneNumber.trim();
        const digitCount = (phone.match(/\d/g) ?? []).length;
        if (!PHONE_CHARACTERS.test(phone) || digitCount < 6 || digitCount > 15) {
            errors.PhoneNumber = 'Enter a valid phone number with 6 to 15 digits';
        }
    }

    if (!blank(values.ContactEmail)) {
        if (values.ContactEmail.trim().length > 254)
            errors.ContactEmail = 'Contact email can be at most 254 characters';
        else if (!EMAIL_PATTERN.test(values.ContactEmail.trim()))
            errors.ContactEmail = 'Enter a valid contact email address';
    }

    if (blank(values.Street1)) errors.Street1 = 'Street is required';

    if (blank(values.ZipCode)) errors.ZipCode = 'Zip code is required';
    else if (!isValidZipCode(values.ZipCode.trim(), countryId))
        errors.ZipCode =
            countryId === 'FI'
                ? 'Zip code must be 5 digits for Finland'
                : 'Zip code must be 3 to 5 digits';

    if (blank(values.City)) errors.City = 'City is required';
    else if (!CITY_PATTERN.test(values.City.trim()))
        errors.City = 'City can only contain letters, spaces, hyphens and apostrophes';

    if (blank(countryId)) errors.CountryId = 'Country is required';

    if (methodId === 0) errors.BillingMethodId = 'Billing method is required';
    else if (billingMethods && !blank(countryId)) {
        const method = billingMethods.find((m) => m.Id === methodId);
        if (!method?.Countries?.includes(countryId))
            errors.BillingMethodId = 'Billing method is not valid for the selected country';
    }

    if (!blank(values.Email)) {
        if (values.Email.trim().length > 254) errors.Email = 'Email can be at most 254 characters';
        else if (!EMAIL_PATTERN.test(values.Email.trim()))
            errors.Email = 'Enter a valid email address';
    }
    if (blank(values.Email) && methodId === BillingMethodId.EmailInvoice)
        errors.Email = 'Email is required for email invoice';

    if (!blank(values.GLN) && values.GLN.trim().length > 35)
        errors.GLN = 'GLN can be at most 35 characters';
    if (methodId === BillingMethodId.ElectronicInvoice) {
        if (blank(values.GLN)) errors.GLN = 'GLN is required for electronic invoice';
        if (blank(values.ReferenceLine1))
            errors.ReferenceLine1 = 'Reference line 1 is required for electronic invoice';
        if (blank(values.ReferenceLine2))
            errors.ReferenceLine2 = 'Reference line 2 is required for electronic invoice';
    }

    if (countryId.toUpperCase() === 'SE' && blank(values.VatRegistrationNumber))
        errors.VatRegistrationNumber = 'VAT registration number is required for Swedish companies';

    const days = values.PaymentTermsDays === '' ? NaN : Number(values.PaymentTermsDays);
    if (!Number.isInteger(days) || days < 0 || days > MAX_PAYMENT_TERMS_DAYS)
        errors.PaymentTermsDays = `Payment terms must be a whole number between 0 and ${MAX_PAYMENT_TERMS_DAYS} days`;

    if (values.LicensePlanId === '') errors.LicensePlanId = 'License plan is required';
    else {
        const planId = Number(values.LicensePlanId);
        if (!SELLABLE_LICENSE_PLAN_IDS.includes(planId) && planId !== currentLicensePlanId)
            errors.LicensePlanId = 'Pick a quarterly or yearly plan';
    }

    return errors;
};

const clean = (value: string) => value.trim();
const cleanOrNull = (value: string) => (blank(value) ? null : value.trim());

export const toFormValues = (info: {
    [K in keyof BillingFormValues]?: BillingFormValues[K] | null;
}): BillingFormValues => ({
    Name: info.Name ?? '',
    FirstName: info.FirstName ?? '',
    LastName: info.LastName ?? '',
    PhoneNumber: info.PhoneNumber ?? '',
    ContactEmail: info.ContactEmail ?? '',
    Attention: info.Attention ?? '',
    Street1: info.Street1 ?? '',
    Street2: info.Street2 ?? '',
    ZipCode: info.ZipCode ?? '',
    City: info.City ?? '',
    CountryId: info.CountryId ?? '',
    Email: info.Email ?? '',
    BillingMethodId: info.BillingMethodId ?? '',
    LicensePlanId: info.LicensePlanId ?? '',
    PaymentTermsDays: info.PaymentTermsDays ?? '',
    VatRegistrationNumber: info.VatRegistrationNumber ?? '',
    GLN: info.GLN ?? '',
    ReferenceLine1: info.ReferenceLine1 ?? '',
    ReferenceLine2: info.ReferenceLine2 ?? '',
});

// Optional fields are sent as null when empty: the API replaces the whole record, so null clears them.
export const toPayload = (companyId: string, values: BillingFormValues) => ({
    CompanyId: companyId,
    Name: clean(values.Name),
    FirstName: clean(values.FirstName),
    LastName: clean(values.LastName),
    PhoneNumber: cleanOrNull(values.PhoneNumber),
    ContactEmail: cleanOrNull(values.ContactEmail),
    Attention: cleanOrNull(values.Attention),
    Street1: clean(values.Street1),
    Street2: cleanOrNull(values.Street2),
    ZipCode: clean(values.ZipCode),
    City: clean(values.City),
    CountryId: clean(values.CountryId),
    Email: cleanOrNull(values.Email),
    BillingMethodId: Number(values.BillingMethodId),
    LicensePlanId: Number(values.LicensePlanId),
    PaymentTermsDays: Number(values.PaymentTermsDays),
    VatRegistrationNumber: cleanOrNull(values.VatRegistrationNumber),
    GLN: cleanOrNull(values.GLN),
    ReferenceLine1: cleanOrNull(values.ReferenceLine1),
    ReferenceLine2: cleanOrNull(values.ReferenceLine2),
});

// Maps the API's field-level validation errors (ServiceStack ResponseStatus) onto form fields so a
// rule the client does not know about still lands next to the right input.
export const mapServerErrors = (error: any): BillingFormErrors => {
    const fieldErrors = error?.data?.ResponseStatus?.Errors ?? error?.data?.responseStatus?.errors;
    const result: BillingFormErrors = {};

    if (!Array.isArray(fieldErrors)) return result;

    fieldErrors.forEach((e: { FieldName?: string; fieldName?: string; Message?: string; message?: string }) => {
        const field = (e.FieldName ?? e.fieldName) as keyof BillingFormValues | undefined;
        const message = e.Message ?? e.message;
        if (field && message && !(field in result)) result[field] = message;
    });

    return result;
};
